/**
 * DeptQuickAccess — the "second line" strip that lives under every
 * department Command Center hero/KPI section. It presents the sections
 * this department OWNS (per the PhD directive: strict ownership, no
 * cross-department leakage).
 *
 * Ownership map (Feb 2026, per CEO directive):
 *   hr               → employees, attendance, blackouts, pricing, contracts
 *   finance          → billing, pricing, contracts
 *   legal            → pricing, contracts, training
 *   sales            → pricing, contracts, training, demo-tools
 *   safety           → training, demo-tools
 *   marketing        → training, demo-tools
 *   customer-service → demo-tools
 *   procurement      → brokers
 *   training         → training (self)
 *
 * Tiles are keyed by the same `activeTab` value used inside the
 * Operations dashboard, so navigating from any Quick Access tile flips
 * the Tabs wrapper to the correct section without duplicating content.
 */
import { Card } from '../ui/card';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { useDepartmentMenu } from './DepartmentNavigation';
import {
  Users, Calendar, CalendarOff, DollarSign, FileText, Receipt,
  GraduationCap, Sparkles, Truck, ArrowRight, Building2, BarChart3, Rocket,
  MessageCircle, MapPin, ClipboardList, UsersRound,
} from 'lucide-react';

// Central catalog of every section a department can own, keyed by tab value.
// `readOnly: true` means "the department can jump into this section from
// their Quick Access but should not mutate it" — the frontend will render
// action buttons as disabled when the current parentDept has `readOnly` set
// for that section. Used for cross-team visibility without ownership.
export const SECTION_META = {
  employees: { label: 'Employees', sub: 'Roster · onboarding · role changes', icon: Users, accent: '#0ea5e9' },
  attendance: { label: 'Attendance', sub: 'Points · warnings · sweep', icon: Calendar, accent: '#22c55e' },
  blackouts: { label: 'Blackouts', sub: 'Platform-wide service pauses', icon: CalendarOff, accent: '#ef4444' },
  pricing: { label: 'Properties · Pricing', sub: 'Package tiers · quote review', icon: DollarSign, accent: '#f59e0b' },
  contracts: { label: 'Properties · Contracts', sub: 'MSAs · service agreements', icon: FileText, accent: '#a855f7' },
  'ops-properties': { label: 'Properties & Service Agreements', sub: 'Auto-posted from Cities Explorer', icon: Building2, accent: '#C9A962' },
  'fleet-safety': { label: 'Fleet Safety & Incidents', sub: 'Vehicles · safety checks · incident reports', icon: Truck, accent: '#ef4444' },
  statistics: { label: 'Service Statistics', sub: 'Ops · Billing · Training · Services', icon: BarChart3, accent: '#FFD60A' },
  billing: { label: 'Billing & A/R', sub: 'Invoices · dunning · payments', icon: Receipt, accent: '#f59e0b' },
  training: { label: 'Updates & Notices', sub: 'Publish updates · training modules', icon: GraduationCap, accent: '#0ea5e9' },
  'demo-tools': { label: 'Demo Tools', sub: 'Live pitch · sales walk-throughs', icon: Sparkles, accent: '#ec4899' },
  brokers: { label: 'Brokers & Providers', sub: 'Suppliers · categories · ledger', icon: Truck, accent: '#a855f7' },
  'sales-leads': { label: 'Sales Leads', sub: 'Inbound demo signups · pipeline', icon: Rocket, accent: '#22c55e' },
  'ops-staffing': { label: 'Staffing Agents', sub: 'Regional coverage · read only from CS/Training', icon: UsersRound, accent: '#0ea5e9' },
  'support-chat': { label: 'Live Chats', sub: 'Real-time visitor + tenant sessions', icon: MessageCircle, accent: '#22c55e' },
  'live-tracking': { label: 'Live Tracking', sub: 'Truck GPS · route progress · ETAs', icon: MapPin, accent: '#0ea5e9' },
  'specialty-services': { label: 'Specialty Services', sub: 'Bulk · pressure wash · commercial add-ons', icon: Sparkles, accent: '#ec4899' },
  reports: { label: 'All Reports', sub: 'Daily · weekly · violation · service reports', icon: ClipboardList, accent: '#a855f7' },
};

// Ownership matrix — strictly authored per the CEO directive.
// Sections listed under `readOnlySections` render as visible tiles that link
// into the target tab in a read-only wrapper (mutation buttons hidden).
export const DEPT_QUICK_ACCESS_MAP = {
  hr: ['employees', 'attendance', 'blackouts', 'pricing', 'contracts', 'ops-properties'],
  finance: ['billing', 'pricing', 'contracts', 'ops-properties', 'statistics'],
  legal: ['pricing', 'contracts', 'training', 'ops-properties'],
  sales: ['sales-leads', 'pricing', 'contracts', 'training', 'demo-tools', 'statistics'],
  safety: ['training', 'demo-tools', 'statistics', 'fleet-safety'],
  marketing: ['sales-leads', 'training', 'demo-tools'],
  // Customer Service — owns visitor + tenant support surfaces; needs eyes
  // (read-only) on Staffing so they can answer "who is coming to my
  // property tonight" without being able to reassign crews.
  'customer-service': ['support-chat', 'live-tracking', 'specialty-services', 'reports', 'sales-leads', 'ops-staffing'],
  procurement: ['brokers', 'fleet-safety'],
  // Training & Development — Sales Leads visibility (for onboarding-content
  // requests from the sales team), read-only Staffing (roster context), and
  // deep-linked training-relevant Operations sections so instructors can
  // walk trainees through the real tools without being able to mutate live
  // ops data.
  training: [
    'training',
    'sales-leads',
    'ops-staffing',
    'live-tracking',
    'specialty-services',
    'reports',
    'demo-tools',
  ],
};

// Sections that Training + Customer Service can VIEW but not mutate.
export const DEPT_READONLY_SECTIONS = {
  training: ['ops-staffing', 'live-tracking', 'specialty-services', 'reports'],
  'customer-service': ['ops-staffing'],
};

/**
 * Renders the Quick Access strip for one department.
 *
 * @param {string} deptKey - one of the keys of DEPT_QUICK_ACCESS_MAP
 * @param {string} accent - hex accent used for the section header rule
 * @param {(sectionKey: string) => void} onOpen - fired when a tile is clicked
 */
export default function DeptQuickAccess({ deptKey, accent = '#C9A962', onOpen }) {
  const terms = useAdminScopeTerms();
  const sections = DEPT_QUICK_ACCESS_MAP[deptKey] || [];
  const choose = useDepartmentMenu('quick-access', 'Menu sections', sections.map(key => ({key, label: terms.text(SECTION_META[key].label), icon: SECTION_META[key].icon})), null,
    key => onOpen?.(key, { readOnly: (DEPT_READONLY_SECTIONS[deptKey] || []).includes(key) }));
  if (sections.length === 0) return null;

  return (
    <section data-testid={`${deptKey}-quick-access`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-px flex-1" style={{ backgroundColor: `${accent}55` }} />
        <h2
          className="text-xs uppercase font-semibold"
          style={{ color: accent }}
        >
          Department Menu Tabs
        </h2>
        <div className="h-px flex-1" style={{ backgroundColor: `${accent}55` }} />
      </div>

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 items-stretch">
        {sections.map((key) => {
          const meta = SECTION_META[key];
          if (!meta) return null;
          const Icon = meta.icon;
          const isReadOnly = (DEPT_READONLY_SECTIONS[deptKey] || []).includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => choose(key)}
              className="group text-left h-full"
              data-testid={`${deptKey}-quick-access-${key}`}
              data-readonly={isReadOnly ? 'true' : 'false'}
            >
              <Card
                // Uniform tile dimensions across every dept Command Center —
                // 160-pixel min-height keeps titles + sub-copy aligned even
                // when copy lengths vary. Read-only tiles get a slim amber
                // badge in the top-right so the operator instantly knows
                // they can look but not mutate.
                className="bg-zinc-900 border border-zinc-700 hover:border-[color:var(--tile-accent)] p-4 h-full min-h-[160px] flex flex-col transition-all group-hover:-translate-y-0.5 group-hover:shadow-lg"
                style={{ '--tile-accent': meta.accent }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${meta.accent}22`, border: `1px solid ${meta.accent}` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: meta.accent }} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isReadOnly && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#78350f', color: '#fbbf24', border: '1px solid #b45309' }}
                        data-testid={`${deptKey}-readonly-badge-${key}`}
                      >
                        Read only
                      </span>
                    )}
                    <ArrowRight
                      className="h-4 w-4 text-zinc-600 group-hover:text-white transition-colors mt-0.5"
                    />
                  </div>
                </div>
                <p
                  className="text-white font-bold mt-3 text-sm leading-tight line-clamp-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}
                >
                  {terms.text(meta.label).toUpperCase()}
                </p>
                <p className="text-[11px] text-zinc-500 mt-1 leading-snug line-clamp-2">{terms.text(meta.sub)}</p>
              </Card>
            </button>
          );
        })}
      </div>
    </section>
  );
}
